import { useState } from 'react';
import {
  Anchor,
  Badge,
  Box,
  Button,
  Flex,
  Paper,
  Popover,
  Stack,
  Text,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { GrLocation } from 'react-icons/gr';
import ModalJoinPremium from '@company/components/Payment/ModalJoinPremium';
import useNotification from '@hooks/useNotification';

export const SUGGESTED_WORKERS_PRICING_URL = 'https://trustedherd.com/pricing';
export const JOB_PASS_COMING_SOON =
  'Coming soon — join 30 day pass from job creation flow to unlock now.';

// Decorative one-off tones from the client-approved mockup; no equivalent
// design tokens exist for these, so they stay local to this component.
const COLORS = {
  gold: '#e8c547',
  cardBodyLight: '#f0ead8',
  radarCenterBg: '#fffdf6',
  radarBorder: '#d4af37',
  pulseRing: 'rgba(242, 208, 50, 0.45)',
};

const RADAR_DOTS = [
  { rotate: 18, color: 'var(--yellow)' },
  { rotate: 72, color: '#7eb8c9' },
  { rotate: 128, color: COLORS.radarBorder },
  { rotate: 188, color: '#9bbf8a' },
  { rotate: 248, color: '#c4a574' },
  { rotate: 312, color: '#8aa4d4' },
];

const PULSE_NAME = 'sw-radar-pulse';
const PULSE = `${PULSE_NAME} 2.4s ease-out infinite`;

interface SuggestedWorkersPaywallProps {
  count: number;
  locationLabel: string;
}

const workerWord = (count: number) => (count === 1 ? 'worker' : 'workers');

function Radar({ count }: { count: number }) {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  return (
    <Box pos="relative" w={120} h={120}>
      <style>{`
        @keyframes ${PULSE_NAME} {
          0% { transform: scale(0.86); opacity: 0.7; }
          100% { transform: scale(1.12); opacity: 0; }
        }
      `}</style>
      <Box
        pos="absolute"
        inset={8}
        bdrs="50%"
        style={{
          border: `1px solid ${COLORS.pulseRing}`,
          animation: reduceMotion ? undefined : PULSE,
        }}
      />
      <Box
        pos="absolute"
        inset={0}
        bdrs="50%"
        style={{
          border: `1px solid ${COLORS.pulseRing}`,
          animation: reduceMotion ? undefined : PULSE,
          animationDelay: reduceMotion ? undefined : '1.2s',
        }}
      />
      <Stack
        pos="absolute"
        inset={18}
        bdrs="50%"
        bg={COLORS.radarCenterBg}
        align="center"
        justify="center"
        gap={0}
        style={{ border: `2px dashed ${COLORS.radarBorder}`, zIndex: 1 }}
      >
        <Text fz={30} fw={800} lh={1} c="var(--black)">
          {count}
        </Text>
        <Text fz={10} fw={700} tt="uppercase" lts={0.6} c="var(--dark-gray)">
          Live nearby
        </Text>
      </Stack>
      {RADAR_DOTS.map((dot) => (
        <Box
          key={dot.rotate}
          pos="absolute"
          top="50%"
          left="50%"
          w={14}
          h={14}
          bdrs="50%"
          bg={dot.color}
          style={{
            margin: '-7px 0 0 -7px',
            border: '2px solid #fff',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.18)',
            transform: `rotate(${dot.rotate}deg) translate(50px)`,
            zIndex: 2,
          }}
        />
      ))}
    </Box>
  );
}

/**
 * Teaser for the Suggested tab when the company cannot see the list.
 * Join Premium opens the existing subscription modal. Monthly Job Pass is
 * coming soon (no React checkout yet) — click shows a popover.
 */
export default function SuggestedWorkersPaywall({
  count,
  locationLabel,
}: SuggestedWorkersPaywallProps) {
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const isMobileLayout = useMediaQuery('(max-width: 560px)');
  const { showErrorNotification } = useNotification();

  const showJoinPremiumMessage = (success: boolean, message: string) => {
    if (success) {
      window.location.reload();
    } else {
      showErrorNotification(message);
    }
  };

  const nearby = locationLabel
    ? `${count} ${workerWord(count)} suggested near ${locationLabel}`
    : `${count} ${workerWord(count)} suggested nearby`;

  const premiumCard = (
    <Paper
      bg="var(--black)"
      p="sm"
      radius="md"
      flex={1}
      display="flex"
      pos="relative"
      style={{ flexDirection: 'column' }}
    >
      <Badge
        pos="absolute"
        top={-12}
        left={16}
        size="md"
        variant="filled"
        color="yellow"
        c="var(--black)"
        tt="uppercase"
        radius="xl"
      >
        Best value
      </Badge>
      <Stack gap="sm" flex={1}>
        <Text fz="lg" fw={800} c={COLORS.gold}>
          Premium
        </Text>
        <Text fz="sm" c={COLORS.cardBodyLight} lh={1.45} flex={1}>
          Unlimited Suggested Workers on every job, plus every other Premium
          benefit for your entire company, including global Search Talent
          database search and contact.
        </Text>
        <Button
          variant="brand"
          fullWidth
          onClick={() => setShowPremiumModal(true)}
        >
          Join Premium →
        </Button>
      </Stack>
    </Paper>
  );

  const jobPassCard = (
    <Paper
      bg="white"
      p="sm"
      radius="md"
      flex={1}
      display="flex"
      withBorder
      style={{ flexDirection: 'column' }}
    >
      <Stack gap="sm" flex={1}>
        <Text fz="lg" fw={800} c="var(--black)">
          Monthly Job Pass
        </Text>
        <Text fz="sm" c="var(--dark-gray)" lh={1.45} flex={1}>
          Unlock Suggested Workers for 30 days. Ideal for single events or a
          busy stretch of hiring.
        </Text>
        <Popover width={260} position="top" withArrow shadow="md">
          <Popover.Target>
            <Button variant="negative-brand" fullWidth>
              Join Monthly Job Pass →
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            <Text size="sm">{JOB_PASS_COMING_SOON}</Text>
          </Popover.Dropdown>
        </Popover>
      </Stack>
    </Paper>
  );

  return (
    <Stack align="center" justify="center" gap="xs" mih="100%" py="xs" px="md">
      <Radar count={count} />

      <Stack gap={4} maw={540} px="xs">
        <Text fz={20} fw={800} ta="center" lh={1.2} c="var(--black)">
          Local candidates, ready for you right now.
        </Text>
        <Text fz="sm" ta="center" c="var(--dark-gray)" lh={1.45}>
          Suggested Workers are talent who&apos;ve paid for extra exposure on
          Trusted Herd — they want to be found, and you can reach out the moment
          you upgrade.
        </Text>
      </Stack>

      <Badge
        variant="light"
        color="teal"
        size="lg"
        radius="sm"
        tt="none"
        fw={600}
        leftSection={<GrLocation size={14} />}
      >
        {nearby}
      </Badge>

      {isMobileLayout ? (
        <Stack gap="md" px="md" w="100%">
          {premiumCard}
          {jobPassCard}
        </Stack>
      ) : (
        <Flex align="stretch" gap="md" px="md" w="100%">
          {premiumCard}
          {jobPassCard}
        </Flex>
      )}

      <Anchor
        href={SUGGESTED_WORKERS_PRICING_URL}
        target="_blank"
        rel="noopener noreferrer"
        fz="sm"
        c="var(--dark-gray)"
        underline="always"
      >
        Get More Info
      </Anchor>

      {showPremiumModal && (
        <ModalJoinPremium
          showMessage={showJoinPremiumMessage}
          setShowModal={setShowPremiumModal}
        />
      )}
    </Stack>
  );
}
